import "./AttachmentViewer.css";
import React, { useState, useRef, useEffect } from "react";
import ReactPlayer from "react-player";

const AttachmentViewer = ({ url, urlList, setShowAttachViewer }) => {
  const [imageList, setImageList] = useState([]);
  const [iframeSrc, setIframeSrc] = useState("");

  const isImageURL = (url) => {
    const imageExtensions = [".jpg", ".jpeg", ".png", ".gif", ".bmp", ".webp"];
    return imageExtensions.some((ext) => url?.toLowerCase().includes(ext));
  };

  const isVideoURL = (url) => {
    const videoExtensions = [".mp4", ".mov", ".avi", ".mkv", ".webm"];
    return videoExtensions.some((ext) => url?.toLowerCase().includes(ext));
  };


  // Extract YouTube video ID and construct a valid video URL
  const isYouTubeLink = (url) =>
    typeof url === "string" &&
    (url.includes("youtube.com") || url.includes("youtu.be"));

  const getYouTubeVideoUrl = (url) => {
    if (!isYouTubeLink(url)) return url;
    let videoId = null;
    // Match youtube.com/watch?v=VIDEOID
    const match1 = url.match(/[?&]v=([^&#]+)/);
    if (match1) videoId = match1[1];
    // Match youtu.be/VIDEOID
    const match2 = url.match(/youtu\.be\/([^?&#]+)/);
    if (match2) videoId = match2[1];
    // Match youtube.com/embed/VIDEOID
    const match3 = url.match(/embed\/([^?&#]+)/);
    if (match3) videoId = match3[1];
    if (videoId) {
      return `https://www.youtube.com/watch?v=${videoId}`;
    }
    return url;
  };

  useEffect(() => {
    if (urlList && Array.isArray(urlList)) {
      const images = urlList.filter((url) => isImageURL(url));
      setImageList(images);
    } else if (url) {
      if (isImageURL(url)) {
        setImageList([url]);
      } else if (isVideoURL(url) || isYouTubeLink(url)) {
        // For YouTube, ensure valid video URL
        setIframeSrc(isYouTubeLink(url) ? getYouTubeVideoUrl(url) : url);
      } else {
        setIframeSrc(url);
      }
    }
  }, [url, urlList]);

  function ImageZoomInOut({
    imageUrl,
    setShowImage,
    images,
    selectedImgaeeIndex,
  }) {
    const [scale, setScale] = useState(1); // Ensure the image starts at scale 1
    const [currentIndex, setCurrentIndex] = useState(selectedImgaeeIndex);
    const [position, setPosition] = useState({ x: 0, y: 0 });
    const imageRef = useRef(null);

    const handleZoomIn = () => {
      setScale((prevScale) => Math.min(prevScale + 0.1, 3)); // Limit zoom-in
    };

    const handleZoomOut = () => {
      setScale((prevScale) => Math.max(prevScale - 0.1, 1)); // Limit zoom-out
    };

    useEffect(() => {
      const image = imageRef.current;
      let isDragging = false;
      let prevPosition = { x: 0, y: 0 };

      const handleMouseDown = (e) => {
        isDragging = true;
        prevPosition = { x: e.clientX, y: e.clientY };
      };

      const handleMouseMove = (e) => {
        if (!isDragging) return;
        const deltaX = e.clientX - prevPosition.x;
        const deltaY = e.clientY - prevPosition.y;
        prevPosition = { x: e.clientX, y: e.clientY };
        setPosition((position) => ({
          x: position.x + deltaX,
          y: position.y + deltaY,
        }));
      };

      const handleMouseUp = () => {
        isDragging = false;
      };

      image?.addEventListener("mousedown", handleMouseDown);
      image?.addEventListener("mousemove", handleMouseMove);
      image?.addEventListener("mouseup", handleMouseUp);

      // Touch events for zooming with touchpad
      const handleTouchStart = (e) => {
        if (e.touches.length === 2) {
          const distance = Math.hypot(
            e.touches[0].clientX - e.touches[1].clientX,
            e.touches[0].clientY - e.touches[1].clientY
          );
          setScale((prevScale) => prevScale * (distance / 100));
        }
      };

      const handleTouchMove = (e) => {
        if (e.touches.length === 2) {
          const distance = Math.hypot(
            e.touches[0].clientX - e.touches[1].clientX,
            e.touches[0].clientY - e.touches[1].clientY
          );
          setScale((prevScale) => prevScale * (distance / 100));
        }
      };

      image?.addEventListener("touchstart", handleTouchStart);
      image?.addEventListener("touchmove", handleTouchMove);

      return () => {
        image?.removeEventListener("mousedown", handleMouseDown);
        image?.removeEventListener("mousemove", handleMouseMove);
        image?.removeEventListener("mouseup", handleMouseUp);
        image?.removeEventListener("touchstart", handleTouchStart);
        image?.removeEventListener("touchmove", handleTouchMove);
      };
    }, [imageRef, scale]);

    return (
      <div className="show-gallery-selected-image">
        <div
          style={{
            height: "100%",
            position: "relative",
            overflow: "hidden",
          }}
        >
          {currentIndex + 1 < images.length && (
            <div
              className="next-img"
              onClick={() => {
                setCurrentIndex(currentIndex + 1);
                setScale(1);
                setPosition({ x: 0, y: 0 });
              }}
            >
              <i className="fa fa-angle-right"></i>
            </div>
          )}
          {currentIndex - 1 > -1 && (
            <div
              className="prev-img"
              onClick={() => {
                setCurrentIndex(currentIndex - 1);
                setScale(1);
                setPosition({ x: 0, y: 0 });
              }}
            >
              <i className="fa fa-angle-left"></i>
            </div>
          )}
          <div className="gallery-zoom-btn-container">
            <div className="gallery-imga-zoom-icon">
              <i
                className="fa fa-search-plus zoom-icons"
                onClick={handleZoomIn}
              ></i>

              <i
                className="fa fa-search-minus zoom-icons"
                onClick={handleZoomOut}
              ></i>

              <i
                className="fa fa-times close-icon"
                onClick={() => setShowImage(false)}
              ></i>
            </div>
          </div>

          <img
            className="gallery-zoom-image-postions"
            ref={imageRef}
            src={
              currentIndex > -1 && currentIndex < images.length
                ? images[currentIndex]
                : imageUrl
            }
            alt=""
            style={{
              transform: `scale(${scale}) translate(${position.x}px, ${position.y}px)`,
              cursor: "move",
              transition: "transform 0.3s ease",
              position: "absolute", // Ensure the image is not in normal document flow
              top: "50%", // Start at the center vertically
              left: "50%", // Start at the center horizontally
              transform: `translate(-50%, -50%) scale(${scale})`, // Keep it centered
            }}
            draggable={false}
          />
        </div>
      </div>
    );
  }

  return imageList.length > 0 ? (
    <ImageZoomInOut
      imageUrl={imageList[0]}
      setShowImage={setShowAttachViewer}
      images={imageList}
      selectedImgaeeIndex={0}
    ></ImageZoomInOut>
  ) : (
    <div
      className={`iframe-modal  ${
        isYouTubeLink(iframeSrc) || isVideoURL(iframeSrc) ? "youtube" : ""
      }`}
    >
      <div className="iframe-container">
        {isYouTubeLink(iframeSrc) || isVideoURL(iframeSrc) ? (
          <ReactPlayer
            className="react-player"
            url={isYouTubeLink(iframeSrc) ? getYouTubeVideoUrl(iframeSrc) : iframeSrc}
            width="100%"
            height="100%"
            controls
          />
        ) : iframeSrc ? (
          <iframe
            src={iframeSrc}
            title="Attachment"
            width="100%"
            height="100%"
            frameBorder="0"
            allowFullScreen
          />
        ) : (
          <p>No valid URL to display.</p>
        )}

        <div className="close" onClick={() => setShowAttachViewer(false)}>
          <i className="fa fa-times"></i>
        </div>
      </div>
    </div>
  );
};

export default AttachmentViewer;
